// externals
import React, { useEffect, useMemo, useRef, useState } from 'react';

// libraries
import { Accordion, AccordionDetails, AccordionSummary } from '@interstate/components/AccordionGroup';
import { Button } from '@interstate/components/Button';
import { DatePicker } from '@interstate/components/DatePicker';
import type { DatePickerEventValue } from '@interstate/components/DatePicker';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { PostalCodeInput } from '@interstate/components/PostalCodeInput';
import { SelectInput } from '@interstate/components/SelectInput';
import { TextInput } from '@interstate/components/TextInput';
import { Typography } from '@interstate/components/Typography';
import { useFormValidationWithState } from '@makemydeal/dr-shared-ui-utils';

// components
import CreditSourceHeader from './CreditSourceHeader';

// utils
import { getMidDayUTCTime } from '../utils/formatUtils';
import { getStateCodesSelectInputValues } from '@makemydeal/dr-shared-ui-utils';
import { mandatoryDetailsValidationConfig } from './MandatoryDetailsCard.validation';

// constants
import { DATE_FORMAT } from '../constants';

// styles
import { StyledBodyText } from './AltSoftPullConsentAlert.style';
import {
    accordionSx,
    StyledButtonWrapper,
    StyledFormGrid,
    StyledFullRow,
    StyledMandatoryDetailsCard
} from './MandatoryDetailsCard.style';

export interface MandatoryDetailsValues {
    streetAddress: string;
    streetAddress2: string;
    city: string;
    state: string;
    postalCode: string;
    dateOfBirth: string;
}

export interface MandatoryDetailsCardProps {
    initialValues?: Partial<MandatoryDetailsValues>;
    onSubmit: (values: MandatoryDetailsValues) => void;
    isSubmitting?: boolean;
}

const BODY_COPY =
    'Credit data shown is currently self-reported. To unlock AI-powered credit insights, ' +
    'add the required customer details: Date of Birth and Full Address. Then click "Update Customer & Initiate Soft Pull." ' +
    "This soft inquiry is used solely for evaluating financing and deal structure options and will not affect the customer's credit score.";

const prefix = 'mandatory-details-card';

const EMPTY_VALUES: MandatoryDetailsValues = {
    streetAddress: '',
    streetAddress2: '',
    city: '',
    state: '',
    postalCode: '',
    dateOfBirth: ''
};

const REQUIRED_FIELDS: (keyof MandatoryDetailsValues)[] = ['streetAddress', 'city', 'state', 'postalCode', 'dateOfBirth'];

const MandatoryDetailsCard: React.FC<MandatoryDetailsCardProps> = ({ initialValues, onSubmit, isSubmitting = false }) => {
    const stateSelectInputValues = useMemo(getStateCodesSelectInputValues, []);
    const interstateTheme = useInterstateTheme();
    const [isExpanded, setIsExpanded] = useState(false);

    const {
        engine: mandatoryDetailsValidator,
        formErrors,
        formValues
    } = useFormValidationWithState(
        {
            initialConfig: mandatoryDetailsValidationConfig,
            initialValues: { ...EMPTY_VALUES, ...initialValues }
        },
        { channel: 'inline' }
    );

    const isFormValid = useMemo(() => {
        const values = formValues as MandatoryDetailsValues;
        const requiredFieldsFilled = REQUIRED_FIELDS.every((field) => Boolean(values[field]));
        const hasErrors = mandatoryDetailsValidator.getHasFormErrors({ channel: 'inline', refresh: false });
        return requiredFieldsFilled && !hasErrors;
    }, [formValues, mandatoryDetailsValidator]);

    // useFormValidationWithState only seeds the engine on first render; later `initialValues` updates are ignored.
    // SPD-backed DOB arrives asynchronously after customerSpdRSAA resolves, so push it in on the empty→populated
    // transition. Gated by prev-ref so user-driven clears or re-edits aren't reverted to the SPD value.
    const prevInitialDob = useRef(initialValues?.dateOfBirth);
    useEffect(() => {
        const incomingDob = initialValues?.dateOfBirth;
        const prev = prevInitialDob.current;
        prevInitialDob.current = incomingDob;
        if (!prev && incomingDob) {
            mandatoryDetailsValidator.setValue('dateOfBirth', incomingDob, { validate: true, clearErrors: false });
        }
    }, [initialValues?.dateOfBirth, mandatoryDetailsValidator]);

    const handleInputChange = (field: keyof MandatoryDetailsValues, value: string) => {
        mandatoryDetailsValidator.setValue(field, value, { validate: true, clearErrors: false });
    };

    const handleDatePickerChange = (field: keyof MandatoryDetailsValues, event: InterstateOnChangeEvent<DatePickerEventValue>) => {
        const value = event.target.value;
        let dateValue = value.dateValue ? value.dateValue : '';

        if (dateValue && field === 'dateOfBirth') {
            dateValue = getMidDayUTCTime(dateValue, DATE_FORMAT) || dateValue;
        }

        handleInputChange(field, dateValue);
    };

    const handleSubmit = () => {
        // `isFormValid` reads the cached error state — pre-populated values can pass that
        // check before the engine has actually run validation on them. validateAll() forces
        // a fresh validation pass, and the early-return catches any errors that surface here.
        mandatoryDetailsValidator.validateAll();
        const hasErrors = mandatoryDetailsValidator.getHasFormErrors({ channel: 'inline', refresh: false });
        if (hasErrors) {
            // Force-open the accordion so the user can see which fields errored — otherwise
            // the button is enabled (cached isFormValid was true), they click, nothing visible
            // changes because the form is still collapsed.
            setIsExpanded(true);
            return;
        }
        onSubmit(formValues as MandatoryDetailsValues);
    };

    const values = formValues as MandatoryDetailsValues;
    const errors = formErrors as Record<keyof MandatoryDetailsValues, { hasError: boolean; errorMessage: string }>;

    const streetAddressField = (
        <TextInput
            size="sm"
            label="Street Address"
            name="streetAddress"
            data-testid={`${prefix}-street-address`}
            value={values.streetAddress}
            hasError={errors.streetAddress.hasError}
            errorMessage={errors.streetAddress.errorMessage}
            onChange={(e) => handleInputChange('streetAddress', e.target.value || '')}
        />
    );

    const streetAddress2Field = (
        <TextInput
            size="sm"
            label="Street Address 2"
            name="streetAddress2"
            data-testid={`${prefix}-street-address-2`}
            value={values.streetAddress2}
            hasError={errors.streetAddress2.hasError}
            errorMessage={errors.streetAddress2.errorMessage}
            onChange={(e) => handleInputChange('streetAddress2', e.target.value || '')}
        />
    );

    const cityField = (
        <TextInput
            size="sm"
            label="City"
            name="city"
            data-testid={`${prefix}-city`}
            value={values.city}
            hasError={errors.city.hasError}
            errorMessage={errors.city.errorMessage}
            onChange={(e) => handleInputChange('city', e.target.value || '')}
        />
    );

    const stateField = (
        <SelectInput
            size="sm"
            label="State"
            name="state"
            data-testid={`${prefix}-state`}
            value={values.state}
            options={stateSelectInputValues}
            displayDeselectOption={false}
            hasError={errors.state.hasError}
            errorMessage={errors.state.errorMessage}
            onChange={(e) => handleInputChange('state', (e.target.value as string) || '')}
        />
    );

    const postalCodeField = (
        <PostalCodeInput
            size="sm"
            country="US"
            label="Postal Code"
            name="postalCode"
            data-testid={`${prefix}-postal-code`}
            value={values.postalCode}
            hasError={errors.postalCode.hasError}
            errorMessage={errors.postalCode.errorMessage}
            onChange={(e) => handleInputChange('postalCode', e.target.value || '')}
        />
    );

    const dateOfBirthField = (
        <DatePicker
            size="sm"
            label="Date of Birth"
            name="dateOfBirth"
            data-testid={`${prefix}-date-of-birth`}
            value={values.dateOfBirth ? new Date(values.dateOfBirth) : undefined}
            hasError={errors.dateOfBirth.hasError}
            errorMessage={errors.dateOfBirth.errorMessage}
            onChange={(e) => handleDatePickerChange('dateOfBirth', e)}
        />
    );

    return (
        <StyledMandatoryDetailsCard data-testid={prefix} interstateTheme={interstateTheme}>
            <CreditSourceHeader dataTestId={`${prefix}-header`} />
            <StyledBodyText data-testid={`${prefix}-body`} variant="body-sm" tag="div">
                {BODY_COPY}
            </StyledBodyText>
            <Accordion
                data-testid={`${prefix}-accordion`}
                expanded={isExpanded}
                onChange={() => setIsExpanded((prev) => !prev)}
                sx={accordionSx}
            >
                <AccordionSummary chevronPosition="left" data-testid={`${prefix}-accordion-summary`}>
                    <Typography variant="body-md" color="base.color.blue.700">
                        Mandatory Details
                    </Typography>
                </AccordionSummary>
                <AccordionDetails data-testid={`${prefix}-accordion-details`}>
                    <StyledFormGrid>
                        <StyledFullRow>{streetAddressField}</StyledFullRow>
                        <StyledFullRow>{streetAddress2Field}</StyledFullRow>
                        {cityField}
                        {stateField}
                        {postalCodeField}
                        {dateOfBirthField}
                    </StyledFormGrid>
                </AccordionDetails>
            </Accordion>
            <StyledButtonWrapper>
                <Button
                    id={`${prefix}-submit-button`}
                    data-testid={`${prefix}-submit-button`}
                    buttonStyle="secondary"
                    size="sm"
                    disabled={!isFormValid || isSubmitting}
                    onClick={handleSubmit}
                >
                    Update Customer & Initiate Soft Pull
                </Button>
            </StyledButtonWrapper>
        </StyledMandatoryDetailsCard>
    );
};

export default MandatoryDetailsCard;
